// Renders one editable frozen-right cell (bool / tri-state / dropdown / text).
// Pulled out of the Pay Code explorer so the same pattern can be reused.
//
// readonly: when true, render the same value as a plain span — no
// click target, no input. Used for view_only role.

const EDITING_PREFIX = 'editing_';

function _displayBool(ec, val) {
  const isTrue = val === true || val === 1;
  if (ec.key === 'ignore_for_billing') return isTrue ? 'Yes' : 'No';
  return isTrue ? 'Y' : 'N';
}

function _displayTri(val) {
  if (val === 'Yes')                return 'Y';
  if (val === 'No')                 return 'N';
  if (val === 'Yes (Worked Only)')  return 'W';
  return '—';
}

export function EditableCell({ row, ec, saving, setSaving, onChange, readonly = false }) {
  const saveKey = `${row.id}_${ec.key}`;
  const disabled = saving === saveKey;

  // Read-only stub when this column only applies to billable rules.
  if (ec.billableOnly && row.ignore_for_billing) {
    const val = row[ec.key];
    return <span style={{ color: '#999', fontSize: 11 }}>{val || '—'}</span>;
  }

  // Role-driven read-only: show a plain span; no editing UI rendered at all.
  if (readonly) {
    const val = row[ec.key];
    let label = '—';
    if (ec.type === 'bool')         label = _displayBool(ec, val);
    else if (ec.type === 'tri')     label = _displayTri(val);
    else if (val != null && val !== '') label = String(val);
    return <span style={{ fontSize: 11, color: '#666' }}>{label}</span>;
  }

  if (ec.type === 'text') {
    const val = row[ec.key] || '';
    const editing = saving === `${EDITING_PREFIX}${row.id}_${ec.key}`;
    if (editing) {
      return (
        <input
          autoFocus
          defaultValue={val}
          onBlur={(e) => {
            setSaving(null);
            if (e.target.value !== val) onChange(row, ec.key, e.target.value || null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') setSaving(null);
          }}
          style={{
            fontSize: 11, padding: '2px 4px', width: '100%',
            borderRadius: 4, border: '1px solid #007bff',
          }}
        />
      );
    }
    return (
      <button
        onClick={() => setSaving(`${EDITING_PREFIX}${row.id}_${ec.key}`)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 11, color: val ? '#333' : '#999', padding: '2px 0',
          width: '100%', textAlign: 'center',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {val || '—'}
      </button>
    );
  }

  if (ec.type === 'bool') {
    const val = row[ec.key];
    const isTrue = val === true || val === 1;
    const label = _displayBool(ec, val);
    return (
      <button
        onClick={() => onChange(row, ec.key, !isTrue)}
        disabled={disabled}
        style={{
          width: 50, padding: '3px 0', fontSize: 11, fontWeight: 600,
          borderRadius: 12, border: 'none', cursor: 'pointer',
          background: isTrue ? '#f8d7da' : '#d4edda',
          color: isTrue ? '#721c24' : '#155724',
        }}
      >
        {label}
      </button>
    );
  }

  if (ec.type === 'tri') {
    const val = row[ec.key] || '';
    const opts = ['', ...ec.options];
    const idx = opts.indexOf(val);
    const nextVal = opts[(idx + 1) % opts.length];
    const display = _displayTri(val);
    const bg  = val === 'Yes' ? '#d4edda' : val === 'No' ? '#f8d7da' : val ? '#fff3cd' : '#e9ecef';
    const clr = val === 'Yes' ? '#155724' : val === 'No' ? '#721c24' : val ? '#856404' : '#999';
    return (
      <button
        onClick={() => onChange(row, ec.key, nextVal || null)}
        disabled={disabled}
        title={val || 'Not set'}
        style={{
          width: 50, padding: '3px 0', fontSize: 11, fontWeight: 600,
          borderRadius: 12, border: 'none', cursor: 'pointer',
          background: bg, color: clr,
        }}
      >
        {display}
      </button>
    );
  }

  if (ec.type === 'dropdown') {
    const val = row[ec.key] || '';
    return (
      <select
        value={val}
        onChange={(e) => onChange(row, ec.key, e.target.value || null)}
        disabled={disabled}
        style={{
          fontSize: 11, padding: '2px 2px', width: '100%',
          borderRadius: 4, border: '1px solid #ccc',
        }}
      >
        <option value="">—</option>
        {ec.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }

  return row[ec.key] ?? '—';
}
